/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useState, useContext, useEffect, useRef, useCallback } from 'react';
import { Spin, Typography, message, Modal } from 'antd';
import { useIntl } from 'react-intl';

import UploadContext from '~src/contexts/UploadContext';
import IconButton from '~components/_common/IconButton/IconButton';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { FileX } from '~src/types/upload';
import { fadeInKeyframes } from '~src/styles/animations';
import ShowUploadDropZoneContext from '~src/contexts/ShowUploadDropZone';
import { DETAILED_UPLOAD_LIMIT } from '~src/config';

const MARGIN = 5;
const PREPARE_CHUNK_SIZE = 200;
const LARGE_UPLOAD_WARNING_LIMIT = 100;

interface UploadDropZonePannelProps {
  onDismiss: () => void;
  disableFolderUpload?: boolean;
  disableFileUpload?: boolean;
  size: 'large' | 'medium' | 'small';
}

type FileSystemEntryWithWebkit = FileSystemEntry & {
  isFile: boolean;
  isDirectory: boolean;
  fullPath: string;
  name: string;
};

type FileSystemFileEntryWithWebkit = FileSystemFileEntry & {
  file: (
    successCallback: (file: File) => void,
    errorCallback?: (err: DOMException) => void
  ) => void;
};

type FileSystemDirectoryEntryWithWebkit = FileSystemDirectoryEntry & {
  createReader: () => FileSystemDirectoryReader;
};

type DataTransferItemWithWebkit = DataTransferItem & {
  webkitGetAsEntry?: () => FileSystemEntryWithWebkit | null;
};

// Builds a stable key for comparing files by name and relative path.
const buildFileKey = (file: FileX) =>
  `${file.name}@${
    file.webkitRelativePath === file.name ? '' : file.webkitRelativePath
  }`;

// Splits a large array into smaller chunks to avoid blocking the UI.
const chunkArray = <T,>(items: T[], chunkSize: number): T[][] => {
  if (chunkSize <= 0) return [items];

  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += chunkSize) {
    chunks.push(items.slice(i, i + chunkSize));
  }

  return chunks;
};

// Ensures each dropped file has a uid used by upload panels and form state.
const ensureFileUid = (file: FileX, index: number): FileX => {
  if (file.uid) {
    return file;
  }

  try {
    Object.defineProperty(file, 'uid', {
      value: `${buildFileKey(file)}-${file.size}-${
        file.lastModified
      }-${Date.now()}-${index}`,
      configurable: true,
    });
  } catch {
    file.uid = `${buildFileKey(file)}-${file.size}-${
      file.lastModified
    }-${Date.now()}-${index}`;
  }

  return file;
};

// Appends new dropped files to the current upload list without duplicating existing ones.
const mergeUploadFiles = (currentFiles: FileX[], incomingFiles: FileX[]) => {
  const fileMap = new Map<string, FileX>();

  currentFiles.forEach((file) => {
    fileMap.set(buildFileKey(file), file);
  });

  incomingFiles.forEach((file, index) => {
    const normalizedFile = ensureFileUid(file, index);
    const key = buildFileKey(normalizedFile);

    if (!fileMap.has(key)) {
      fileMap.set(key, normalizedFile);
    }
  });

  return Array.from(fileMap.values());
};

// Shows a confirmation before switching from detailed upload to bulk upload mode.
const confirmSwitchToBulkUpload = (
  intl: ReturnType<typeof useIntl>,
  totalFiles: number
) =>
  new Promise<boolean>((resolve) => {
    const isLargeUpload = totalFiles >= LARGE_UPLOAD_WARNING_LIMIT;

    Modal.confirm({
      title: intl.formatMessage({
        id: isLargeUpload
          ? 'large-upload-confirm-title'
          : 'bulk-upload-switch-confirm-title',
      }),
      content: isLargeUpload ? (
        <div>
          <Typography.Paragraph>
            {intl.formatMessage(
              { id: 'large-upload-confirm-description' },
              { total: totalFiles }
            )}
          </Typography.Paragraph>

          <Typography.Paragraph style={{ marginBottom: 0 }}>
            {intl.formatMessage({
              id: 'large-upload-confirm-resume-description',
            })}
          </Typography.Paragraph>
        </div>
      ) : (
        intl.formatMessage(
          { id: 'bulk-upload-switch-confirm-description' },
          {
            limit: DETAILED_UPLOAD_LIMIT,
            total: totalFiles,
          }
        )
      ),
      okText: intl.formatMessage({ id: 'continue' }),
      cancelText: intl.formatMessage({ id: 'cancel' }),
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
    });
  });

// Gives the browser a chance to render between heavy async file-processing steps.
const yieldToMainThread = () =>
  new Promise<void>((resolve) => window.setTimeout(resolve, 0));

// Reads a dropped file entry and preserves its relative folder path.
const readFileEntry = (
  entry: FileSystemFileEntryWithWebkit
): Promise<FileX | null> =>
  new Promise((resolve) => {
    entry.file(
      (file) => {
        const relativePath = entry.fullPath?.startsWith('/')
          ? entry.fullPath.slice(1)
          : entry.fullPath || file.name;

        try {
          Object.defineProperty(file, 'webkitRelativePath', {
            value: relativePath || file.name,
            configurable: true,
          });
        } catch {
          (file as FileX).webkitRelativePath = relativePath || file.name;
        }

        resolve(file as FileX);
      },
      () => resolve(null)
    );
  });

// Reads all batches from a directory reader until the browser returns no more entries.
const readAllDirectoryEntries = async (
  reader: FileSystemDirectoryReader
): Promise<FileSystemEntryWithWebkit[]> => {
  const entries: FileSystemEntryWithWebkit[] = [];

  while (true) {
    const batch = await new Promise<FileSystemEntryWithWebkit[]>((resolve) => {
      reader.readEntries((result) =>
        resolve(result as FileSystemEntryWithWebkit[])
      );
    });

    if (!batch.length) {
      break;
    }

    entries.push(...batch);
  }

  return entries;
};

// Recursively walks files and folders from a dropped entry tree.
const walkEntry = async (
  entry: FileSystemEntryWithWebkit,
  collectedFiles: FileX[],
  onProgress?: (count: number) => void
): Promise<void> => {
  if (entry.isFile) {
    const file = await readFileEntry(entry as FileSystemFileEntryWithWebkit);
    if (file) {
      collectedFiles.push(file);
      onProgress?.(collectedFiles.length);
    }
    return;
  }

  if (entry.isDirectory) {
    const reader = (entry as FileSystemDirectoryEntryWithWebkit).createReader();
    const children = await readAllDirectoryEntries(reader);

    for (const child of children) {
      await walkEntry(child, collectedFiles, onProgress);
    }
  }
};

// Extracts files from a drop event, including recursively dropped folders when supported.
const extractDroppedFiles = async (
  event: React.DragEvent<HTMLDivElement>,
  onProgress?: (count: number) => void
): Promise<{ files: FileX[]; hasDirectoryEntry: boolean }> => {
  const items = Array.from(
    event.dataTransfer.items || []
  ) as DataTransferItemWithWebkit[];

  if (!items.length) {
    return {
      files: Array.from(event.dataTransfer.files || []) as FileX[],
      hasDirectoryEntry: false,
    };
  }

  const collectedFiles: FileX[] = [];
  const entries = items
    .map((item) => item.webkitGetAsEntry?.())
    .filter(Boolean) as FileSystemEntryWithWebkit[];

  if (!entries.length) {
    return {
      files: Array.from(event.dataTransfer.files || []) as FileX[],
      hasDirectoryEntry: false,
    };
  }

  for (const entry of entries) {
    await walkEntry(entry, collectedFiles, onProgress);
    await yieldToMainThread();
  }

  return {
    files: collectedFiles,
    hasDirectoryEntry: entries.some((entry) => entry.isDirectory),
  };
};

const UploadDropZonePannel: React.FC<UploadDropZonePannelProps> = ({
  onDismiss,
  disableFolderUpload,
  disableFileUpload,
  size,
}) => {
  const style = useStyles(styleFn);
  const intl = useIntl();

  const [spinState, setSpinState] = useState(false);
  const [isPreparing, setIsPreparing] = useState(false);
  const [preparingMessage, setPreparingMessage] = useState('');
  const [preparedCount, setPreparedCount] = useState(0);
  const [dragState, setDragState] = useState<'idle' | 'over'>('idle');

  const { uploadContext, setUploadContext } = useContext(UploadContext);
  const filesRef = useRef<FileX[]>([]);
  const updateTimeoutRef = useRef<number>();
  const isPreparingRef = useRef(false);
  const dragCounterRef = useRef(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const { setShowUploadZone } = useContext(ShowUploadDropZoneContext);

  useEffect(() => {
    const handleLeaveWindow = (e: DragEvent) => {
      if (
        e.clientY <= 0 ||
        e.clientX <= 0 ||
        e.clientX >= window.innerWidth ||
        e.clientY >= window.innerHeight
      ) {
        doDismiss();
      }
    };

    const handleKey = (e: KeyboardEvent) => {
      if (e.code === 'Escape' || e.keyCode === 27) {
        doDismiss();
      }
    };

    window.addEventListener('blur', doDismiss);
    window.addEventListener('dragleave', handleLeaveWindow);
    window.addEventListener('keydown', handleKey);
    window.addEventListener('click', doDismiss);

    return () => {
      window.removeEventListener('blur', doDismiss);
      window.removeEventListener('dragleave', handleLeaveWindow);
      window.removeEventListener('keydown', handleKey);
      window.removeEventListener('click', doDismiss);
    };
  }, []);

  const doDismiss = () => {
    if (!updateTimeoutRef.current && !isPreparingRef.current) {
      onDismiss();
    }
  };

  const processFilesInChunks = useCallback(
    async (incomingFiles: FileX[]) => {
      setSpinState(true);
      setIsPreparing(true);
      isPreparingRef.current = true;
      setPreparingMessage(
        intl.formatMessage({ id: 'preparing-large-upload' }) ||
          'Preparing upload...'
      );
      setPreparedCount(0);

      try {
        if (
          disableFolderUpload &&
          incomingFiles.some(
            (file) =>
              file.webkitRelativePath && file.webkitRelativePath !== file.name
          )
        ) {
          message.error(intl.formatMessage({ id: 'permission-restricted' }));
          setShowUploadZone(false);
          onDismiss();
          return;
        }

        if (
          disableFileUpload &&
          incomingFiles.some(
            (file) =>
              !file.webkitRelativePath ||
              file.webkitRelativePath.split('/').length === 1
          )
        ) {
          message.error(intl.formatMessage({ id: 'file-upload-root-error' }));
          setShowUploadZone(false);
          onDismiss();
          return;
        }

        const chunks = chunkArray(incomingFiles, PREPARE_CHUNK_SIZE);
        const fileMap = new Map<string, FileX>();
        let processed = 0;

        for (const chunk of chunks) {
          chunk.forEach((file) => {
            const key = buildFileKey(file);
            if (!fileMap.has(key)) {
              fileMap.set(key, file);
            }
          });

          processed += chunk.length;
          setPreparedCount(processed);
          setPreparingMessage(
            intl.formatMessage(
              { id: 'scanning-upload-files-progress' },
              { count: processed }
            ) || `Scanning files... ${processed}`
          );

          await yieldToMainThread();
        }

        const finalFiles = Array.from(fileMap.values()).map(ensureFileUid);

        setPreparingMessage(
          intl.formatMessage({ id: 'preparing-upload-structure' }) ||
            'Preparing upload structure...'
        );

        await yieldToMainThread();

        const mergedFiles = mergeUploadFiles(uploadContext || [], finalFiles);
        const shouldSwitchToBulk = mergedFiles.length > DETAILED_UPLOAD_LIMIT;

        if (shouldSwitchToBulk) {
          const confirmed = await confirmSwitchToBulkUpload(
            intl,
            mergedFiles.length
          );

          if (!confirmed) {
            setShowUploadZone(false);
            onDismiss();
            return;
          }
        }

        setUploadContext(mergedFiles);
        setShowUploadZone(false);
        onDismiss();
      } finally {
        filesRef.current = [];
        updateTimeoutRef.current = undefined;
        isPreparingRef.current = false;
        setIsPreparing(false);
        setSpinState(false);
        setDragState('idle');
        dragCounterRef.current = 0;
      }
    },
    [
      disableFolderUpload,
      disableFileUpload,
      intl,
      onDismiss,
      setShowUploadZone,
      setUploadContext,
      uploadContext,
    ]
  );

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (dragState !== 'over') {
      setDragState('over');
    }
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    dragCounterRef.current += 1;
    setDragState('over');
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    dragCounterRef.current -= 1;

    if (dragCounterRef.current <= 0) {
      dragCounterRef.current = 0;
      setDragState('idle');
    }
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    dragCounterRef.current = 0;
    setDragState('idle');
    setSpinState(true);
    setIsPreparing(true);
    isPreparingRef.current = true;
    setPreparingMessage(intl.formatMessage({ id: 'scanning-large-upload' }));
    setPreparedCount(0);

    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }

    try {
      const {
        files: droppedFiles,
        hasDirectoryEntry,
      } = await extractDroppedFiles(e, (count) => {
        setPreparedCount(count);
        setPreparingMessage(
          intl.formatMessage(
            { id: 'scanning-upload-files-progress' },
            { count }
          ) || `Scanning files... ${count}`
        );
      });

      if (!droppedFiles.length) {
        setSpinState(false);
        setIsPreparing(false);
        isPreparingRef.current = false;
        message.warning(
          intl.formatMessage({
            id: hasDirectoryEntry
              ? 'empty-folder-drop-warning'
              : 'empty-drop-warning',
          })
        );
        setShowUploadZone(false);
        onDismiss();
        return;
      }

      filesRef.current = droppedFiles;

      updateTimeoutRef.current = window.setTimeout(async () => {
        const currentFiles = [...filesRef.current];
        await processFilesInChunks(currentFiles);
      }, 50);
    } catch (err) {
      setSpinState(false);
      setIsPreparing(false);
      isPreparingRef.current = false;
      message.error(intl.formatMessage({ id: 'generic-error' }));
    }
  };

  return (
    <div
      ref={containerRef}
      css={[
        style.container,
        {
          top: size === 'small' ? 0 : MARGIN,
          height: size === 'small' ? '100%' : `calc(100% - ${MARGIN * 2}px)`,
        },
      ]}>
      <div
        css={[
          style.centerContentContainer,
          dragState === 'over' ? style.overContainer : undefined,
        ]}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}>
        <div
          id="zone"
          css={[
            style.dropZone,
            { padding: size === 'small' ? 0 : size === 'medium' ? 2 : 20 },
          ]}>
          {size !== 'small' && (
            <IconButton
              iconStyle="fad"
              icon="file-upload"
              customStyle={[
                style.uploadZoneIcon,
                { fontSize: size === 'medium' ? '20px' : '70px' },
              ]}
              backgroundOff
            />
          )}

          {!isPreparing &&
            ((size === 'small' && !spinState) ||
              size === 'medium' ||
              size === 'large') && (
              <Typography.Paragraph
                strong
                css={[
                  style.uploadZoneText,
                  {
                    marginTop:
                      size === 'small'
                        ? '5px'
                        : size === 'medium'
                        ? '2px'
                        : '16px',
                    padding: size === 'small' ? 0 : size === 'medium' ? 2 : 10,
                    fontSize:
                      size === 'small'
                        ? '10px'
                        : size === 'medium'
                        ? '10px'
                        : '17px',
                  },
                ]}>
                {intl.formatMessage({ id: 'drag-drop-here' })}
              </Typography.Paragraph>
            )}

          {isPreparing && (
            <div css={style.preparingContainer}>
              <Typography.Paragraph strong css={style.preparingTitle}>
                {intl.formatMessage({ id: 'scanning-large-upload' }) ||
                  'Scanning large upload...'}
              </Typography.Paragraph>

              <Typography.Paragraph css={style.preparingText}>
                {preparingMessage}
              </Typography.Paragraph>

              <Typography.Paragraph css={style.preparingCount}>
                {intl.formatMessage(
                  { id: 'files-processed-count' },
                  { count: preparedCount }
                ) || `${preparedCount} files processed`}
              </Typography.Paragraph>
            </div>
          )}

          <div
            css={{
              minHeight: size === 'medium' ? 5 : 26,
              minWidth: size === 'medium' ? 5 : 26,
            }}>
            {spinState ? <Spin /> : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadDropZonePannel;

const styleFn: ThemedStyleFn = (theme) => ({
  container: {
    position: 'absolute',
    top: MARGIN,
    left: MARGIN,
    width: `calc(100% - ${MARGIN * 2}px)`,
    height: `calc(100% - ${MARGIN * 2}px)`,
    animation: `${fadeInKeyframes} 0.2s ease-out`,
  },
  text: {
    display: 'flex',
    flexDirection: 'column',
  },
  div: {
    color: theme.colors.text.primary,
    padding: '6px 0px',
  },
  properties: {
    color: theme.colors.text.secondary,
    backgroundColor: theme.colors.lightGreen,
  },
  pannelContainer: {
    marginTop: '-35px',
  },
  pannelTitle: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    fontWeight: 'bold',
    color: theme.colors.text.primary,
  },
  centerContentContainer: {
    background: 'rgba(250, 250, 250, 0.85)',
    borderWidth: 2,
    borderStyle: 'dashed',
    borderColor: 'rgba(0, 0, 0, 0.15)',
    borderRadius: 4,
    width: '100%',
    height: '100%',
    transition: 'all 0.15s ease',
  },
  overContainer: {
    borderColor: theme.colors.primary,
    background: 'rgba(250, 250, 250, 0.95)',
  },
  dropZone: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100%',
  },
  uploadZoneIcon: {
    color: theme.colors.primary,
    fontSize: '70px',
  },
  uploadZoneText: {
    fontSize: '17px',
    padding: 10,
    borderRadius: 6,
    background: 'rgb(250, 250, 250)',
  },
  preparingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: 12,
    maxWidth: 420,
  },
  preparingTitle: {
    marginBottom: 6,
    color: theme.colors.text.primary,
  },
  preparingText: {
    marginBottom: 4,
    color: theme.colors.text.secondary,
  },
  preparingCount: {
    marginBottom: 0,
    color: theme.colors.text.primary,
    fontWeight: 600,
  },
  removeIcon: {
    color: theme.colors.red,
  },
  checkbox: {
    marginTop: '3px',
    alignSelf: 'flex-start',
  },
  buttons: {
    margin: '10px 10px 0 10px',
  },
});
